import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  MenuItem
} from '@mui/material';
import api from '@/services/auth.service';

// AIDEV-NOTE: Helper para extrair digito de uma string por indice (evita undefined em strict mode)
function digitAt(str: string, index: number): number {
  return parseInt(str.charAt(index), 10);
}

// AIDEV-NOTE: Validacao de CPF (11 digitos) com algoritmo de digito verificador
function validarCPF(value: string): boolean {
  const clean = value.replace(/\D/g, '');
  if (clean.length !== 11 || /^(\d)\1+$/.test(clean)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) sum += digitAt(clean, i) * (10 - i);
  let r = (sum * 10) % 11;
  if (r === 10 || r === 11) r = 0;
  if (r !== digitAt(clean, 9)) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) sum += digitAt(clean, i) * (11 - i);
  r = (sum * 10) % 11;
  if (r === 10 || r === 11) r = 0;
  return r === digitAt(clean, 10);
}

// AIDEV-NOTE: Validacao de CNPJ (14 digitos) com algoritmo de digito verificador
function validarCNPJ(value: string): boolean {
  const clean = value.replace(/\D/g, '');
  if (clean.length !== 14 || /^(\d)\1+$/.test(clean)) return false;

  const calcDigit = (cnpj: string, weights: number[]) => {
    const sum = weights.reduce((acc, w, i) => acc + w * digitAt(cnpj, i), 0);
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };

  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  return calcDigit(clean, w1) === digitAt(clean, 12) && calcDigit(clean, w2) === digitAt(clean, 13);
}

// AIDEV-NOTE: Valida CPF ou CNPJ baseado no comprimento do documento limpo
function validarCpfCnpj(value: string): boolean {
  const clean = value.replace(/\D/g, '');
  if (clean.length === 11) return validarCPF(clean);
  if (clean.length === 14) return validarCNPJ(clean);
  return false;
}

const UFS_BRASIL = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO'
];

const novoClienteSchema = z.object({
  nome: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres'),
  cpf_cnpj: z
    .string()
    .min(11, 'CPF/CNPJ invalido')
    .refine(validarCpfCnpj, { message: 'CPF/CNPJ invalido' }),
  telefone: z.string().min(8, 'Telefone obrigatorio'),
  email: z.union([z.string().email('E-mail invalido'), z.literal('')]),
  inscricao_estadual: z.string().max(30, 'Maximo 30 caracteres').optional(),
  cep: z.string().regex(/^\d{8}$/, 'CEP deve ter 8 digitos'),
  endereco: z.string().min(3, 'Endereco obrigatorio'),
  numero: z.string().min(1, 'Numero obrigatorio'),
  bairro: z.string().min(2, 'Bairro obrigatorio'),
  cidade: z.string().min(2, 'Cidade obrigatoria'),
  uf: z.string().length(2, 'UF deve ter 2 caracteres')
});

type NovoClienteForm = z.infer<typeof novoClienteSchema>;

interface NovoClienteModalProps {
  open: boolean;
  onClose: () => void;
  onClienteCriado: (cliente: {
    CODIGO: number;
    RAZAO_SOCIAL: string;
    CNPJ_CPF: string;
    CIDADE: string;
  }) => void;
}

// AIDEV-NOTE: Modal para criar novo cliente rapido durante o fluxo de pedido.
// Verifica duplicidade via POST /api/clientes/verificar antes de salvar.
// Inclui campos de endereco obrigatorios (cep, endereco, numero, bairro).
export function NovoClienteModal({ open, onClose, onClienteCriado }: NovoClienteModalProps) {
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<NovoClienteForm>({
    resolver: zodResolver(novoClienteSchema),
    defaultValues: {
      nome: '',
      cpf_cnpj: '',
      telefone: '',
      email: '',
      inscricao_estadual: '',
      cep: '',
      endereco: '',
      numero: '',
      bairro: '',
      cidade: '',
      uf: ''
    }
  });

  const handleClose = () => {
    reset();
    setSubmitError(null);
    onClose();
  };

  const onSubmit = async (data: NovoClienteForm) => {
    try {
      setSubmitError(null);

      // AIDEV-NOTE: Verifica se CPF/CNPJ ja existe antes de criar
      try {
        await api.post('/clientes/verificar', {
          cpf_cnpj: data.cpf_cnpj.replace(/\D/g, '')
        });
      } catch (err: unknown) {
        if (
          typeof err === 'object' &&
          err !== null &&
          'response' in err &&
          (err as { response?: { status?: number } }).response?.status === 409
        ) {
          setSubmitError('Cliente com este CPF/CNPJ ja esta cadastrado.');
          return;
        }
        // Se nao for 409, a verificacao nao encontrou duplicata — prosseguir
      }

      const response = await api.post<{
        cliente: { CODIGO: number; RAZAO_SOCIAL: string; CNPJ_CPF: string; CIDADE: string };
      }>('/clientes', {
        nome: data.nome,
        cpf_cnpj: data.cpf_cnpj.replace(/\D/g, ''),
        telefone: data.telefone,
        email: data.email,
        // AIDEV-NOTE: opcional — string vazia vira null (coluna ja existe em sync_clientes)
        inscricao_estadual: data.inscricao_estadual || null,
        cep: data.cep,
        endereco: data.endereco,
        numero: data.numero,
        bairro: data.bairro,
        cidade: data.cidade,
        uf: data.uf
      });

      onClienteCriado(response.data.cliente);
      handleClose();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Erro ao cadastrar cliente. Tente novamente.'
      );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>Novo Cliente</DialogTitle>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Controller
                name="nome"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nome / Razao Social *"
                    fullWidth
                    error={!!errors.nome}
                    helperText={errors.nome?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="cpf_cnpj"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="CPF/CNPJ *"
                    fullWidth
                    error={!!errors.cpf_cnpj}
                    helperText={errors.cpf_cnpj?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="inscricao_estadual"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Inscricao Estadual"
                    fullWidth
                    error={!!errors.inscricao_estadual}
                    helperText={errors.inscricao_estadual?.message}
                    inputProps={{ maxLength: 30 }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="telefone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Telefone *"
                    fullWidth
                    error={!!errors.telefone}
                    helperText={errors.telefone?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="E-mail"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Grid>
            {/* AIDEV-NOTE: Campos de endereco obrigatorios para cadastro completo */}
            <Grid item xs={12} sm={3}>
              <Controller
                name="cep"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="CEP *"
                    fullWidth
                    error={!!errors.cep}
                    helperText={errors.cep?.message}
                    inputProps={{ maxLength: 8 }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="endereco"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Endereco *"
                    fullWidth
                    error={!!errors.endereco}
                    helperText={errors.endereco?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Controller
                name="numero"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Numero *"
                    fullWidth
                    error={!!errors.numero}
                    helperText={errors.numero?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="bairro"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Bairro *"
                    fullWidth
                    error={!!errors.bairro}
                    helperText={errors.bairro?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Controller
                name="cidade"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Cidade *"
                    fullWidth
                    error={!!errors.cidade}
                    helperText={errors.cidade?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Controller
                name="uf"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="UF *"
                    fullWidth
                    error={!!errors.uf}
                    helperText={errors.uf?.message}
                  >
                    <MenuItem value="">--</MenuItem>
                    {UFS_BRASIL.map((uf) => (
                      <MenuItem key={uf} value={uf}>
                        {uf}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Salvando...' : 'Cadastrar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
